import { formatNumberWithBankersRounding } from "$root/localization/formatters";
import { type IconName, Icon } from "@mdotm/shadcn/legacy/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

export function InfoDelta({
	diff,
	enh,
	style,
	className,
	zeroAsRemoved = true,
	decimalPlaces = 2,
}: {
	diff: number;
	enh: number;
	decimalPlaces?: number;
	className?: string;
	zeroAsRemoved?: boolean;
	style?: CSSProperties;
}): React.JSX.Element {
	const { t } = useTranslation();

	const state:
		| {
				type: "increased";
				text: string;
		  }
		| {
				type: "removed";
				text: string;
		  }
		| {
				type: "reduced";
				text: string;
		  }
		| null =
		diff > 0
			? {
					type: "increased",
					text: `+${formatNumberWithBankersRounding(diff, decimalPlaces)}%`,
				}
			: diff < 0 && enh === 0 && zeroAsRemoved
				? {
						type: "removed",
						text: t("EDIT_COMPOSITION.TABLE.REMOVED"),
					}
				: diff < 0
					? {
							type: "reduced",
							text: `${formatNumberWithBankersRounding(diff, decimalPlaces)}%`,
						}
					: null;

	return (
		<>
			{!state ? (
				<></>
			) : (
				<div className={overrideClassName("w-full flex items-center gap-0.5", className)} style={style}>
					<DeltaIcon type={state.type} />
					<div className="w-14 text-right tabular-nums">{state.text}</div>
				</div>
			)}
		</>
	);
}

/** Which way a delta moved: up, down, or down to nothing. */
export type DeltaDirection = "increased" | "reduced" | "removed";

const directionIcon: Record<DeltaDirection, { icon: IconName; color: string }> = {
	increased: { icon: "Up1", color: themeCSSVars.palette_P500 },
	removed: { icon: "Icon-full-error", color: themeCSSVars.palette_D500 },
	reduced: { icon: "Down1", color: themeCSSVars.palette_A600 },
};

/** The arrow {@link InfoDelta} puts before a delta, for a delta cell that formats its own number. */
export function DeltaIcon({ type }: { type: DeltaDirection }): React.JSX.Element {
	return <Icon icon={directionIcon[type].icon} color={directionIcon[type].color} size={16} />;
}
